import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { campaignApi } from '../lib/api'
import type {
  SaveItineraryWeekRequest,
  UpdateAssignmentStatusRequest,
  ToggleAvailabilityRequest,
  ScheduleAnalysisRequest,
} from '../types/campaign'

export function useItineraryWeek(campaignId: string | undefined, weekStart: string) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'itinerary', weekStart],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID is required')
      const response = await campaignApi.getItineraryWeek(campaignId, weekStart)
      return response.data
    },
    enabled: !!campaignId,
  })
}

export function useSaveItineraryWeek(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveItineraryWeekRequest) =>
      campaignApi.saveItineraryWeek(campaignId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'itinerary'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'stats'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'turfs'] })
    },
  })
}

export function useUpdateAssignmentStatus(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ assignmentId, data }: { assignmentId: string; data: UpdateAssignmentStatusRequest }) =>
      campaignApi.updateAssignmentStatus(campaignId, assignmentId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'itinerary'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'stats'] })
    },
  })
}

export function useToggleAvailability(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ToggleAvailabilityRequest) =>
      campaignApi.toggleAvailability(campaignId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'itinerary'] })
    },
  })
}

export function useScheduleAnalysis(
  campaignId: string | undefined,
  request: ScheduleAnalysisRequest | null,
) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'schedule-analysis', request?.weekStart, request?.dayTimeOverrides],
    queryFn: async () => {
      if (!campaignId || !request) throw new Error('Missing params')
      const response = await campaignApi.getScheduleAnalysis(campaignId, request)
      return response.data
    },
    enabled: !!campaignId && !!request,
    staleTime: 2 * 60 * 1000,
  })
}
